import type { OUTLIER_CALCULATOR_STRINGS } from "@/components/tools/outlier-calculator";
import {
  Explanation,
  Formula,
  ResultTable,
} from "@/components/tools/stats-descriptive/ui";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPValue,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  DIXON_MAX_N,
  DIXON_MIN_N,
  type DixonLevel,
  type FlagRow,
  type GrubbsStep,
  MODIFIED_Z_CUTOFF,
  type OutlierReport,
} from "@/lib/tool-kit/stats-descriptive-more/outliers";

type Strings = typeof OUTLIER_CALCULATOR_STRINGS;

export interface View {
  strings: Strings;
  fmt: NumberFormatter;
  num: (value: number) => string;
  list: (values: readonly number[]) => string;
}

function confidenceLabel(level: DixonLevel, strings: Strings): string {
  if (level === 0.1) {
    return strings.confidence90;
  }
  return level === 0.05 ? strings.confidence95 : strings.confidence99;
}

export function flaggedText(values: readonly number[], view: View): string {
  return values.length === 0 ? view.strings.none : view.list(values);
}

function maxAbs(scores: readonly number[]): number {
  return scores.reduce((best, score) => Math.max(best, Math.abs(score)), 0);
}

export function methodRows(
  report: OutlierReport,
  alpha: DixonLevel,
  view: View
) {
  const { strings, num } = view;
  const { tukey, z, modified, grubbs, dixon } = report;
  const first = grubbs[0];
  const alphaText = num(alpha);
  return [
    [
      strings.methodTukeyName,
      t(strings.ruleTukey, {
        lower: num(tukey.lower),
        upper: num(tukey.upper),
      }),
      flaggedText(tukey.flagged, view),
    ],
    [
      strings.methodZName,
      z ? t(strings.ruleZ, { cutoff: num(z.cutoff) }) : "—",
      z ? flaggedText(z.flagged, view) : strings.notAvailableZ,
    ],
    [
      strings.methodModifiedName,
      modified
        ? t(strings.ruleModified, { cutoff: num(modified.cutoff) })
        : "—",
      modified
        ? flaggedText(modified.flagged, view)
        : strings.notAvailableModified,
    ],
    [
      strings.methodGrubbsName,
      first
        ? t(strings.ruleGrubbs, {
            critical: num(first.critical),
            alpha: alphaText,
          })
        : "—",
      first
        ? flaggedText(report.grubbsFlagged, view)
        : strings.notAvailableGrubbs,
    ],
    [
      strings.methodDixonName,
      dixon
        ? t(strings.ruleDixon, {
            critical: num(dixon.critical),
            alpha: alphaText,
          })
        : "—",
      dixon
        ? flaggedText(report.dixonFlagged, view)
        : t(strings.notAvailableDixon, {
            min: view.fmt(DIXON_MIN_N),
            max: view.fmt(DIXON_MAX_N),
          }),
    ],
  ];
}

function cell(applies: boolean, hit: boolean, strings: Strings): string {
  if (!applies) {
    return strings.notApplicable;
  }
  return hit ? strings.flagged : strings.notFlagged;
}

export function valueRows(
  report: OutlierReport,
  rows: readonly FlagRow[],
  view: View
) {
  const { strings, fmt, num } = view;
  return rows.map((row) => [
    num(row.value),
    fmt(row.count),
    cell(true, row.tukey, strings),
    cell(report.z !== null, row.z, strings),
    cell(report.modified !== null, row.modified, strings),
    cell(report.grubbs.length > 0, row.grubbs, strings),
    cell(report.dixon !== null, row.dixon, strings),
  ]);
}

function grubbsRows(steps: readonly GrubbsStep[], view: View) {
  const { strings, fmt, num } = view;
  return steps.map((step, index) => [
    fmt(index + 1),
    fmt(step.n),
    num(step.suspect),
    num(step.g),
    num(step.critical),
    formatPValue(fmt, step.p),
    step.significant ? strings.resultOutlier : strings.resultKept,
  ]);
}

export function TukeyWorking({
  report,
  k,
  view,
}: {
  report: OutlierReport;
  k: string;
  view: View;
}) {
  const { strings, num } = view;
  const { tukey } = report;
  return (
    <div className="space-y-2">
      <h4 className="font-medium text-foreground text-sm">
        {strings.methodTukeyName}
      </h4>
      <Formula>
        {t(strings.tukeyQuartiles, {
          q1: num(tukey.q1),
          q3: num(tukey.q3),
          iqr: num(tukey.iqr),
        })}
      </Formula>
      <Formula>
        {t(strings.tukeyLower, {
          k,
          q1: num(tukey.q1),
          iqr: num(tukey.iqr),
          fence: num(tukey.lower),
        })}
      </Formula>
      <Formula>
        {t(strings.tukeyUpper, {
          k,
          q3: num(tukey.q3),
          iqr: num(tukey.iqr),
          fence: num(tukey.upper),
        })}
      </Formula>
      <Explanation>
        {t(strings.tukeyExtreme, {
          lower: num(tukey.outerLower),
          upper: num(tukey.outerUpper),
          list: flaggedText(tukey.extreme, view),
        })}
      </Explanation>
    </div>
  );
}

export function ScoreWorking({
  report,
  view,
}: {
  report: OutlierReport;
  view: View;
}) {
  const { strings, num, fmt } = view;
  const { z, modified } = report;
  return (
    <>
      {z ? (
        <div className="space-y-2">
          <h4 className="font-medium text-foreground text-sm">
            {strings.methodZName}
          </h4>
          <Formula>
            {t(strings.zMeanSd, { mean: num(z.mean), sd: num(z.sd) })}
          </Formula>
          <Formula>
            {t(strings.zRule, {
              cutoff: num(z.cutoff),
              max: num(maxAbs(z.scores)),
            })}
          </Formula>
          {z.cutoff >= z.maxPossible ? (
            <Explanation>
              {t(strings.zCeiling, {
                n: fmt(report.n),
                ceiling: num(z.maxPossible),
                cutoff: num(z.cutoff),
              })}
            </Explanation>
          ) : null}
        </div>
      ) : null}
      {modified ? (
        <div className="space-y-2">
          <h4 className="font-medium text-foreground text-sm">
            {strings.methodModifiedName}
          </h4>
          <Formula>
            {t(strings.modifiedMedian, {
              median: num(modified.median),
              mad: num(modified.mad),
            })}
          </Formula>
          <Formula>
            {t(
              modified.usedMeanAd
                ? strings.modifiedMeanAd
                : strings.modifiedRule,
              {
                cutoff: num(MODIFIED_Z_CUTOFF),
                max: num(maxAbs(modified.scores)),
              }
            )}
          </Formula>
        </div>
      ) : null}
    </>
  );
}

export function TestWorking({
  report,
  alpha,
  view,
}: {
  report: OutlierReport;
  alpha: DixonLevel;
  view: View;
}) {
  const { strings, num, fmt } = view;
  const first = report.grubbs[0];
  const { dixon } = report;
  return (
    <>
      {first ? (
        <div className="space-y-2">
          <h4 className="font-medium text-foreground text-sm">
            {strings.methodGrubbsName}
          </h4>
          <Formula>
            {t(strings.grubbsFormula, {
              suspect: num(first.suspect),
              mean: num(first.mean),
              sd: num(first.sd),
              g: num(first.g),
              n: fmt(first.n),
            })}
          </Formula>
          <Formula>
            {t(strings.grubbsCritical, {
              t: num(first.t),
              critical: num(first.critical),
            })}
          </Formula>
          <h5 className="text-muted-foreground text-xs">
            {strings.grubbsTableTitle}
          </h5>
          <ResultTable
            head={[
              strings.colStep,
              strings.colN,
              strings.colSuspect,
              strings.colG,
              strings.colCritical,
              strings.colP,
              strings.colResult,
            ]}
            rows={grubbsRows(report.grubbs, view)}
          />
        </div>
      ) : null}
      {dixon ? (
        <div className="space-y-2">
          <h4 className="font-medium text-foreground text-sm">
            {strings.methodDixonName}
          </h4>
          <Formula>
            {t(strings.dixonFormula, {
              suspect: num(dixon.suspect),
              neighbour: num(dixon.neighbour),
              max: num(report.data[report.n - 1]),
              min: num(report.data[0]),
              q: num(dixon.q),
            })}
          </Formula>
          <Formula>
            {t(strings.dixonCritical, {
              n: fmt(dixon.n),
              confidence: confidenceLabel(alpha, strings),
              critical: num(dixon.critical),
            })}
          </Formula>
          <Explanation>
            {dixon.significant
              ? t(strings.dixonSignificant, { value: num(dixon.suspect) })
              : strings.dixonKept}
          </Explanation>
        </div>
      ) : null}
    </>
  );
}
